/**
 * WaiverApproval - public page where a parent/guardian reviews and signs the sponsored-tutoring
 * waiver via an emailed one-time token. Signing requires the guardian to be authenticated (their own
 * adult account); if not signed in, we prompt them to sign in or create a guardian account first.
 */
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle, Loader2, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import consentService from '../services/consentService'
import ConsentForm from '../components/consent/ConsentForm'
import { useAuthStore } from '../stores/authStore'

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl w-full"
    >
      {children}
    </motion.div>
  </div>
)

const WaiverApproval = () => {
  const { token } = useParams()
  const user = useAuthStore((state) => state.user)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [request, setRequest] = useState(null)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null) // 'approved' | 'declined'
  const [confirmingDecline, setConfirmingDecline] = useState(false)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        setLoading(true)
        const res = await consentService.getWaiverByToken(token)
        if (active) setRequest(res.data.request)
      } catch (err) {
        if (active) setError(err.response?.data?.error || 'Invalid or expired waiver link')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [token])

  const handleSubmit = async ({ consentTypes, signatureName }) => {
    try {
      setProcessing(true)
      await consentService.acceptWaiverByToken(token, { consentTypes, signatureName })
      setDone('approved')
      toast.success('Consent recorded. A confirmation has been emailed to you.')
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Please sign in or create your guardian account to sign the waiver.')
      } else {
        toast.error(err.response?.data?.error || 'Failed to record consent')
      }
    } finally {
      setProcessing(false)
    }
  }

  const handleDecline = async () => {
    try {
      setProcessing(true)
      await consentService.declineWaiverByToken(token)
      setDone('declined')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to decline')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <Shell>
        <div className="text-center py-8">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading waiver...</p>
        </div>
      </Shell>
    )
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Invalid Link</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
          >
            Go to Home
          </Link>
        </div>
      </Shell>
    )
  }

  if (done === 'approved') {
    return (
      <Shell>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Consent Recorded
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Thank you. {request?.student?.name || 'Your student'} is now cleared to join sponsored
            tutoring events. A signed copy has been emailed to you.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
          >
            Go to Home
          </Link>
        </div>
      </Shell>
    )
  }

  if (done === 'declined') {
    return (
      <Shell>
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-gray-600 dark:text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Request Declined
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You have declined this waiver. No consent was recorded.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
          >
            Go to Home
          </Link>
        </div>
      </Shell>
    )
  }

  const studentName =
    request?.student?.name ||
    `${request?.student?.firstName || ''} ${request?.student?.lastName || ''}`.trim()

  return (
    <Shell>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          Tutoring Waiver Approval
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Review and sign to allow your student to join sponsored tutoring.
        </p>
      </div>

      {!user && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-5 text-sm text-amber-800 dark:text-amber-200">
          To sign this waiver you must be signed in to your own parent/guardian account.{' '}
          <Link to={`/login?redirect=/waiver-approval/${token}`} className="underline font-medium">
            Sign in
          </Link>{' '}
          or{' '}
          <Link to="/register" className="underline font-medium">
            create a guardian account
          </Link>{' '}
          first.
        </div>
      )}

      {/* Both signing and declining require the guardian's own authenticated account (enforced
          server-side). Don't render the actionable form/buttons until signed in - the banner above
          prompts sign-in - so this legal e-signature form can't be filled out only to 401. */}
      {user && (
        <>
          <ConsentForm
            document={request?.document}
            studentName={studentName}
            onSubmit={handleSubmit}
            processing={processing}
            submitLabel="Sign and Submit Consent"
          />

          {/* Two-step confirm: declining is a legally meaningful action that requires the student to
              send a new request to recover, so the first tap reveals an explicit confirm/cancel. */}
          {confirmingDecline ? (
            <div className="mt-3 flex items-center justify-center gap-4 text-sm">
              <span className="text-gray-600 dark:text-gray-400">Decline this waiver request?</span>
              <button
                onClick={handleDecline}
                disabled={processing}
                className="px-4 py-1.5 rounded-lg text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
              >
                Yes, decline
              </button>
              <button
                onClick={() => setConfirmingDecline(false)}
                disabled={processing}
                className="px-4 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDecline(true)}
              disabled={processing}
              className="w-full mt-3 px-6 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
            >
              Decline this request
            </button>
          )}
        </>
      )}

      <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4">
        This link expires on{' '}
        {request?.expiresAt ? new Date(request.expiresAt).toLocaleString() : 'soon'}.
      </p>
    </Shell>
  )
}

export default WaiverApproval
